import type { FAQType } from '../../components/faq'
import ActionableLocationIcon from '../../icons/location-based/actionable-location'
import AutomatedDataIcon from '../../icons/location-based/automated-data'
import EnhancedDataIcon from '../../icons/location-based/enhanced-data'
import GeoDataMonitorIcon from '../../icons/location-based/geo-data-monitor'
import IntegratedDataIcon from '../../icons/location-based/integrated-data'
import LocationBasedIcon from '../../icons/location-based/location-based'
import LocationInsightsIcon from '../../icons/location-based/location-insights'
import ProactiveMarketIcon from '../../icons/location-based/proactive-market'
import ProductBasedIcon from '../../icons/location-based/product-based'
import RealTimeInventoryIcon from '../../icons/location-based/real-time-inventory'
import RealTimeTrendIcon from '../../icons/location-based/real-time-trend'
import StrategicInventoryIcon from '../../icons/location-based/strategic-inventory'
import TrendVisualizationIcon from '../../icons/location-based/trend-visualization'
import type {
  CardType,
  InfiniteScrollType,
  KeyFeatureType,
  SolutionEmpowersType,
  Stat,
} from '../solutions'

const LOCATION_BASED_LOGOS: InfiniteScrollType[] = [
  {
    src: '/images/solutions/brands/johnson-johnson.png',
    alt: 'Johnson & Johnson',
    width: 207,
  },
  {
    src: '/images/solutions/brands/colgate.png',
    alt: 'Colgate',
    width: 92,
  },
  {
    src: '/images/solutions/brands/fellas.png',
    alt: 'Fellas',
    width: 121,
  },
  {
    src: '/images/solutions/brands/getir.png',
    alt: 'Getir',
    width: 84,
  },
  {
    src: '/images/solutions/brands/cci.png',
    alt: 'Coca Cola Icecek',
    width: 72,
  },
  {
    src: '/images/solutions/brands/naos.png',
    alt: 'Naos',
    width: 162,
  },
  {
    src: '/images/solutions/brands/eti.png',
    alt: 'Eti',
    width: 62,
  },
]

const CARDS: CardType[] = [
  {
    title: 'Geo-Data Monitoring',
    content:
      'Track product availability in real time across warehouses and hubs.',
    Icon: GeoDataMonitorIcon,
  },
  {
    title: 'Location Insights',
    content:
      'Gain valuable data on categories, subcategories, and SKUs per location.',
    Icon: LocationInsightsIcon,
  },
  {
    title: 'Trend Visualization',
    content:
      'Visualize location efficiency and product availability trends clearly.',
    Icon: TrendVisualizationIcon,
  },
]

const LOCATION_BASED_KEY_FEATURES: KeyFeatureType[] = [
  {
    key: 'location-based-analytics',
    title: 'Location-Based Analytics',
    content:
      'Master location-specific market trends with our cutting-edge location-based analytics. This solution is critical for CPG brands. It provides accurate measurement of product availability across various warehouses and online retailer locations. Get insights into geographical distribution, consumer behavior, and customize inventory strategies to meet specific market demands effectively.',
    Icon: LocationBasedIcon,
  },
  {
    key: 'product-based-analytics',
    title: 'Product-Based Analytics',
    content:
      'Optimize stock management with product-focused analytics. Empower your CPG brand to monitor product availability across multiple locations. Manage your inventory strategically and respond to the market effectively. Analyze product demand patterns and align your supply chain to meet regional customer needs',
    Icon: ProductBasedIcon,
  },
  {
    key: 'integrated-data-visualization',
    title: 'Integrated Data Visualization',
    content:
      'Experience a seamless overview of both location and product data with our integrated visualization capabilities. These powerful visualizations simplify complex data and help you identify key trends, market opportunities, and operational areas for improvement. Make informed decisions with clarity and confidence, backed by visually intuitive data representations.',
    Icon: IntegratedDataIcon,
  },
  {
    key: 'real-time-trend-monitoring',
    title: 'Real-Time Trend Monitoring',
    content:
      'Stay proactive and adaptive with real-time monitoring of market and product trends. Our solution provides up-to-the-minute insights, so you can react to changing customer preferences, market dynamics, and logistical challenges. Real-time location data is vital for maintaining optimal inventory levels. It also helps in capitalizing on emerging market opportunities',
    Icon: RealTimeTrendIcon,
  },
]

const LOCATION_BASED_SOLUTION_EMPOWERS: SolutionEmpowersType[] = [
  {
    key: 'actionable-location-insights',
    title: 'Actionable Location Insights',
    content: 'Generate insights from precise, location-based data.',
    Icon: ActionableLocationIcon,
  },
  {
    key: 'proactive-market-response',
    title: 'Proactive Market Response',
    content: 'Respond swiftly to market and inventory fluctuations.',
    Icon: ProactiveMarketIcon,
  },
  {
    key: 'strategy-optimization',
    title: 'Strategic Inventory Management',
    content: 'Tailor inventory strategies to local demands.',
    Icon: StrategicInventoryIcon,
  },
  {
    key: 'automated-data-analysis',
    title: 'Automated Data Analysis',
    content: 'Upgrade from manual and Excel to automated analysis.',
    Icon: AutomatedDataIcon,
  },
  {
    key: 'enhanced-data-visualization',
    title: 'Enhanced Data Visualization',
    content: 'Streamline decision-making through intuitive visuals.',
    Icon: EnhancedDataIcon,
  },
  {
    key: 'real-time-inventory-updates',
    title: 'Real-Time Inventory Updates',
    content: 'Proactively manage inventory with continuous tracking.',
    Icon: RealTimeInventoryIcon,
  },
]

const LOCATION_BASED_FAQS: FAQType['items'] = [
  {
    trigger:
      "How does Mindsite's Location-Based Analytics help develop market strategies?",
    content:
      'Our solution leverages advanced analytics to provide insights into geographical market trends and consumer behavior. It enables CPG brands to analyze product distribution and performance across different regions, facilitating strategic decision-making and market strategy development.',
    key: 'item-1',
  },
  {
    trigger: 'Why is location-based data crucial for inventory management?',
    content:
      'Location-based data is key to understanding regional market demands and ensuring efficient inventory management. It aligns product availability with consumer demands in different areas while reducing overstock and understock risks.',
    key: 'item-2',
  },
  {
    trigger:
      "What advantages does Mindsite's Location-Based Analytics offer over traditional methods?",
    content:
      "Mindsite's Location-Based Analytics replaces manual, Excel-based processes with real-time, automated data analysis. This provides a more accurate, comprehensive view of market trends and product availability, enhances decision-making, and saves significant time and resources.",
    key: 'item-3',
  },
  {
    trigger:
      'Can the Location-Based Analytics Solution help in responding to market changes?',
    content:
      'Yes, the solution is designed for rapid response to market dynamics. It offers real-time insights into product availability and consumer demands, giving businesses the ability to react faster to changes and capitalize on emerging opportunities.',
    key: 'item-4',
  },
  {
    trigger:
      'How does the solution facilitate proactive problem-solving in inventory management?',
    content:
      'By providing 24/7 tracking and historical trend analysis, the solution helps identify potential stock challenges before they occur. It enables businesses to proactively address issues, minimize stockouts, and maintain consistent product availability.',
    key: 'item-5',
  },
]

const LOCATION_BASED_STATS: Stat[] = [
  {
    value: '90%',
    info: 'decrease in delisting risk.',
  },
  {
    value: '25%',
    info: 'reduction in stock-out situations.',
  },
  {
    value: '20%',
    info: 'sales boost from fewer stockouts.',
  },
]

export {
  CARDS,
  LOCATION_BASED_FAQS,
  LOCATION_BASED_KEY_FEATURES,
  LOCATION_BASED_LOGOS,
  LOCATION_BASED_SOLUTION_EMPOWERS,
  LOCATION_BASED_STATS,
}
